import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../components/common'
import Navigator from '../../../components/common/Navigator'
import InternshipBg from '../../../assets/internship-bg.png'
import { useInternshipList } from './internship-form/api'
import { useAuthStore } from '../../../store/authStore'

interface Internship {
  id: string
  title: string
  application_status: 'new' | 'active' | 'expired' | 'applied'
  end_date: string
  organisation: string
  description: string
  link: string
}

const Internships = () => {
  const { data: response, isLoading, isError } = useInternshipList()
  const { authenticated, userData } = useAuthStore() // Get userData directly from auth store

  const navigate = useNavigate()

  const internships: Internship[] = response?.data || []

  const statusStyles = {
    new: 'bg-BgGreen text-LightGreen',
    active: 'bg-BgGreen text-LightGreen',
    expired: 'bg-statusButtonBg text-titleBorder',
    applied: 'bg-statusButtonBg text-titleBorder',
  }

  const handleLearnMore = (internshipId: string) => {
    if (authenticated) {
      navigate('/information/internship-form', {
        state: {
          prefilledData: {
            // Map the userData to the fields your form expects
            id: userData.id,
            name: userData.name,
            email: userData.email,
            mobile: userData.mobile,
            // Add any other fields your form needs
            internshipId: internshipId,
          },
        },
      })
      console.log('Navigating to internship form with prefilled data:', {
        ...userData,
        internshipId,
      })
    } else {
      navigate('/login')
    }
  }

  if (isLoading) {
    return (
      <section className="py-12 px-4">
        <p>Loading...</p>
      </section>
    )
  }

  if (isError) {
    return (
      <section className="py-12 px-4">
        <p>Error loading data</p>
      </section>
    )
  }

  if (internships.length === 0) {
    return (
      <section className="py-12 px-4">
        <p>No internships found.</p>
      </section>
    )
  }

  return (
    <div className="lg:w-11/12 mx-auto p-4 lg:p-0 lg:my-8">
      <Navigator title="Internships" slug="INTERNSHIPS" />
      <h1 className="text-4xl font-semibold text-black mb-2">Internship</h1>
      <p className="text-sm text-gray-900 mb-4">
        Gain hands-on experience, build real-world skills, and contribute to
        impactful projects through our internship program
      </p>
      <hr className="border-t-1 border-primaryBlue mb-6" />

      <div
        className="bg-cover bg-center bg-no-repeat lg:py-10"
        style={{
          backgroundImage: `url(${InternshipBg})`,
          borderRadius: '10px',
        }}
      >
        <div className="flex items-center justify-center py-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full px-14">
            {internships.map((item) => (
              <div key={item.id} className="bg-white rounded-md p-6 shadow-xl">
                <div className="mb-2">
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded-full ${
                      statusStyles[item.application_status]
                    }`}
                  >
                    {item.application_status}
                  </span>
                </div>
                <h2 className="font-barlow font-medium text-[19px] leading-[19px] tracking-normal text-black mb-4 capitalize">
                  {item.title}
                </h2>
                <div>
                  <div className="flex gap-4 items-center w-full mb-4">
                    <span className="font-barlow font-semibold text-[12px] text-red-600">
                      Close Registration:
                    </span>
                    {/* <span className="font-barlow font-semibold text-[12px] text-red-600">
                      {new Date(item.end_date).toLocaleDateString()}
                    </span> */}
                    <span className="font-barlow font-semibold text-[12px] text-red-600">
                      {new Date(item.end_date).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  <div className="flex lg:flex-row flex-col gap-8 w-full">
                    <div className="flex flex-col w-full">
                      <div className="flex w-full gap-8 mb-2">
                        <span className="font-barlow font-light text-[12px]">
                          Organization:
                        </span>
                        <span className="font-barlow font-light text-[12px]">
                          {item.organisation || 'N/A'}
                        </span>
                      </div>
                      <div className="flex w-full">
                        <span className="font-barlow font-light text-[12px]">
                          {item.description || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex lg:justify-start justify-center">
                  <Button
                    label="Learn More"
                    size="common"
                    outlined
                    disabled={item.application_status === 'applied'}
                    onClick={() => handleLearnMore(item.id)}
                    className="border border-LightGreen text-LightGreen px-8 py-3 hover:bg-LightGreen hover:text-white"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Internships
